import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserPlus,
  MessageSquare,
  MessageCircle,
  Clock,
  CheckCircle,
  Calendar,
  Image,
  FileText,
  History,
  ChevronLeft,
  ChevronRight,
  Ruler,
  Phone
} from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';
import AudioPlayer from '../Common/AudioPlayer';
import { useAuth } from '../../context/AuthContext';

// Icon-Mapping
const getIcon = (iconName) => {
  const icons = {
    'user-plus': UserPlus,
    'message-square': MessageSquare,
    'clock': Clock,
    'check-circle': CheckCircle,
    'calendar': Calendar,
    'image': Image,
    'file-text': FileText,
    'ruler': Ruler,
    'phone': Phone,
    'message-circle': MessageCircle
  };
  return icons[iconName] || MessageSquare;
};

// Farb-Mapping für Corporate Design
const getFarbeClasses = (farbe) => {
  const farben = {
    'primary': {
      bg: 'bg-primary-light',
      icon: 'text-primary',
      border: 'border-primary',
      dot: 'bg-primary'
    },
    'blue': {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      dot: 'bg-blue-500'
    },
    'green': {
      bg: 'bg-green-50',
      icon: 'text-green-600',
      border: 'border-green-200',
      dot: 'bg-green-500'
    },
    'yellow': {
      bg: 'bg-yellow-50',
      icon: 'text-yellow-600',
      border: 'border-yellow-200',
      dot: 'bg-yellow-500'
    },
    'orange': {
      bg: 'bg-orange-50',
      icon: 'text-orange-600',
      border: 'border-orange-200',
      dot: 'bg-orange-500'
    },
    'purple': {
      bg: 'bg-purple-50',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      dot: 'bg-purple-500'
    }
  };
  return farben[farbe] || farben['primary'];
};

// Datum formatieren (kurz für horizontale Darstellung)
const formatDatum = (datum) => {
  const d = new Date(datum);
  const jetzt = new Date();

  // Auf Kalendertage normalisieren (Mitternacht)
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  const datumTag = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffTage = Math.floor((heute - datumTag) / (1000 * 60 * 60 * 24));

  if (diffTage === 0) {
    return `Heute, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffTage === 1) {
    return `Gestern`;
  } else if (diffTage < 7) {
    return `Vor ${diffTage} Tagen`;
  } else {
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  }
};

// Einzelne Timeline-Aktivität (horizontal)
function TimelineItem({ aktivitaet, index, onAnrufClick }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const IconComponent = getIcon(aktivitaet.icon);
  const farbe = getFarbeClasses(aktivitaet.farbe);
  const isAnruf = aktivitaet.typ === 'anruf';

  const handleIconClick = () => {
    // Bei Anrufen: Modal öffnen
    if (isAnruf && onAnrufClick) {
      onAnrufClick(aktivitaet.referenzId);
      return;
    }
    // Sonst: Tooltip anzeigen
    if (aktivitaet.erstelltVon) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex-shrink-0 w-48 relative ${isAnruf ? 'cursor-pointer' : ''}`}
      onClick={isAnruf && onAnrufClick ? () => onAnrufClick(aktivitaet.referenzId) : undefined}
    >
      {/* Icon und Verbindungslinie */}
      <div className="flex items-center mb-3">
        <div className="relative">
          <div
            className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${farbe.bg} flex items-center justify-center border-2 ${farbe.border} cursor-pointer transition-transform active:scale-95 ${isAnruf ? 'hover:scale-110' : ''}`}
            onClick={(e) => { e.stopPropagation(); handleIconClick(); }}
          >
            <IconComponent className={`w-5 h-5 ${farbe.icon}`} />
          </div>

          {/* Popup-Tooltip bei Klick */}
          <AnimatePresence>
            {showTooltip && aktivitaet.erstelltVon && (
              <motion.div
                initial={{ opacity: 0, y: 5, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 5, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="absolute left-1/2 -translate-x-1/2 top-12 z-20 bg-gray-800 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap"
              >
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                {aktivitaet.erstelltVon}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {/* Horizontale Linie nach rechts */}
        <div className="flex-1 h-0.5 bg-gray-200 ml-1" />
      </div>

      {/* Inhalt */}
      <div className="pr-4">
        <p className="text-xs text-gray-400 mb-1">{formatDatum(aktivitaet.datum)}</p>
        <h4 className="font-medium text-gray-900 text-sm leading-tight">{aktivitaet.titel}</h4>
        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{aktivitaet.beschreibung}</p>

        {/* Kategorie Tag */}
        {aktivitaet.kategorie && (
          <span className="inline-block text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full mt-2">
            {aktivitaet.kategorie}
          </span>
        )}

        {/* Klick-Hinweis für Anrufe */}
        {isAnruf && (
          <span className="inline-block text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full mt-2">
            Klicken für Transkript
          </span>
        )}
      </div>
    </motion.div>
  );
}

function AktivitaetenTimeline({ kundeId, refreshTrigger = 0 }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const assistantLabel = isHeykikiOrg ? 'Kiki' : 'KI-Assistent';
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);

  // Anruf-Modal State
  const [selectedAnruf, setSelectedAnruf] = useState(null);
  const [anrufLoading, setAnrufLoading] = useState(false);

  // Anruf-Details laden
  const handleAnrufClick = async (anrufId) => {
    setAnrufLoading(true);
    try {
      const response = await apiClient.get(`/voice-assistant/call-logs/${anrufId}`);
      setSelectedAnruf(response.data);
    } catch (err) {
      console.error('Anruf laden fehlgeschlagen:', err);
    } finally {
      setAnrufLoading(false);
    }
  };

  const loadAktivitaeten = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await apiClient.get(`/kunden/${kundeId}/aktivitaeten`);
      setAktivitaeten(response.data);
      setError(null);
    } catch (err) {
      console.error('Aktivitäten laden fehlgeschlagen:', err);
      setError('Aktivitäten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [kundeId]);

  // Initial laden und bei refreshTrigger-Änderung neu laden
  useEffect(() => {
    loadAktivitaeten(aktivitaeten.length === 0);
  }, [kundeId, refreshTrigger]);

  // Scroll-Status prüfen
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [aktivitaeten]);

  // Beim Laden automatisch zum neuesten Eintrag (rechts) scrollen
  useEffect(() => {
    if (aktivitaeten.length > 0 && !loading) {
      const container = scrollContainerRef.current;
      if (container) {
        // Kurz warten bis das Rendering abgeschlossen ist
        requestAnimationFrame(() => {
          container.scrollLeft = container.scrollWidth;
          checkScroll();
        });
      }
    }
  }, [aktivitaeten.length, loading]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Aktivitäten</h2>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Aktivitäten</h2>
        </div>
        <p className="text-red-500 text-center py-4">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Aktivitäten</h2>
          <span className="text-sm text-gray-400">({aktivitaeten.length})</span>
        </div>

        {/* Scroll-Buttons */}
        {aktivitaeten.length > 0 && (
          <div className="flex gap-1">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-1.5 rounded-lg transition ${
                canScrollLeft
                  ? 'hover:bg-gray-100 text-gray-600'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-1.5 rounded-lg transition ${
                canScrollRight
                  ? 'hover:bg-gray-100 text-gray-600'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Horizontaler Timeline Container */}
      {aktivitaeten.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Noch keine Aktivitäten vorhanden</p>
        </div>
      ) : (
        <div className="relative">
          {/* Scroll-Schatten links */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          )}

          {/* Scroll-Schatten rechts */}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          )}

          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            style={{ scrollbarWidth: 'thin' }}
          >
            {aktivitaeten.map((aktivitaet, index) => (
              <TimelineItem
                key={aktivitaet.id}
                aktivitaet={aktivitaet}
                index={index}
                onAnrufClick={handleAnrufClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Anruf-Transkript Modal */}
      <AnimatePresence>
        {(selectedAnruf || anrufLoading) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedAnruf(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {anrufLoading ? (
                <div className="p-8 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : selectedAnruf && (
                <>
                  {/* Header */}
                  <div className="bg-green-50 p-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Anruf-Transkript</h3>
                        <p className="text-sm text-gray-600">
                          {new Date(selectedAnruf.createdAt).toLocaleString('de-DE')} • {selectedAnruf.callerNumber}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedAnruf(null)}
                      className="p-2 hover:bg-green-100 rounded-lg transition"
                    >
                      <ChevronRight className="w-5 h-5 rotate-45" />
                    </button>
                  </div>

                  {/* Aufzeichnung */}
                  {selectedAnruf.recordingUrl && (
                    <div className="p-4 border-b">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Aufzeichnung</h4>
                      <AudioPlayer src={selectedAnruf.recordingUrl} />
                    </div>
                  )}

                  {/* Zusammenfassung */}
                  {selectedAnruf.summary && !selectedAnruf.summary.includes('Bitte füge den Text') && (
                    <div className="p-4 bg-gray-50 border-b">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Zusammenfassung</h4>
                      <p className="text-gray-900">{selectedAnruf.summary}</p>
                    </div>
                  )}

                  {/* Transkript */}
                  <div className="p-4 overflow-y-auto flex-1 min-h-0">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Transkript</h4>
                    {selectedAnruf.transcript ? (
                      <div className="space-y-3">
                        {selectedAnruf.transcript.split('\n').map((line, i) => {
                          const isAssistent = line.startsWith('Assistent:') || line.startsWith('AI:') || line.startsWith('Agent:') || line.startsWith('KI-Assistent:');
                          const isAnrufer = line.startsWith('Anrufer:') || line.startsWith('User:') || line.startsWith('Kunde:');
                          const text = line.replace(/^(KI-Assistent|Assistent|Anrufer|Agent|Kunde|AI|User):\s*/, '');

                          if (!text.trim()) return null;

                          return (
                            <div
                              key={i}
                              className={`flex ${isAssistent ? 'justify-start' : 'justify-end'}`}
                            >
                              <div
                                className={`max-w-[90%] relative px-4 py-3 rounded-lg ${
                                  isAssistent
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'bg-green-100 text-green-900'
                                }`}
                              >
                                {/* Kiki-Avatar bei HeyKiki-Orgs an Assistant-Bubble */}
                                {isAssistent && isHeykikiOrg && (
                                  <img
                                    src="/heykiki-chat-logo.png"
                                    alt="Kiki"
                                    className="absolute -top-2.5 -left-2.5 w-8 h-8 rounded-full border-2 border-white shadow-sm bg-white object-cover"
                                  />
                                )}
                                {/* Schrift hochgezogen für Handwerker-Lesbarkeit
                                    (Lucas-Feedback 2026-05-07) */}
                                <p className="text-xs font-medium mb-1 opacity-70">
                                  {isAssistent ? assistantLabel : 'Anrufer'}
                                </p>
                                <p className="text-base leading-relaxed">{text}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Kein Transkript verfügbar</p>
                    )}
                  </div>

                  {/* Footer mit Dauer */}
                  <div className="p-4 bg-gray-50 border-t flex items-center justify-between text-sm text-gray-600">
                    <span>
                      Dauer: {selectedAnruf.durationSeconds
                        ? `${Math.floor(selectedAnruf.durationSeconds / 60)}:${String(selectedAnruf.durationSeconds % 60).padStart(2, '0')} Min`
                        : 'Unbekannt'}
                    </span>
                    <span>Status: {selectedAnruf.status}</span>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AktivitaetenTimeline;
